/**
 * Seeded Randomization Utilities for Experimental Auditability
 * 
 * Provides reproducible randomization for email presentation order
 * while maintaining proper statistical properties.
 */

/**
 * Mulberry32 PRNG - Fast, high-quality seeded random number generator
 * Based on Tommy Ettinger's implementation
 */
export class SeededRandom {
  private seed: number;
  
  constructor(seed: string | number) {
    // Convert string to numeric seed
    if (typeof seed === 'string') {
      this.seed = seed.split('').reduce((acc, char) => {
        return ((acc << 5) - acc + char.charCodeAt(0)) >>> 0;
      }, 0);
    } else {
      this.seed = seed >>> 0; // Ensure positive 32-bit integer
    }
  }
  
  /**
   * Generate next random number in [0, 1)
   */
  next(): number {
    this.seed |= 0;
    this.seed = (this.seed + 0x6d2b79f5) | 0;
    let t = Math.imul(this.seed ^ (this.seed >>> 15), this.seed | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  
  /**
   * Generate random integer in [min, max)
   */
  nextInt(min: number, max: number): number {
    return Math.floor(this.next() * (max - min)) + min;
  }
  
  /**
   * Fisher-Yates shuffle with seeded randomization
   */
  shuffle<T>(array: T[]): T[] {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = this.nextInt(0, i + 1);
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }
  
  /**
   * Get current seed state for logging
   */
  getCurrentSeed(): number {
    return this.seed;
  }
}

/**
 * Generate deterministic seed from participant ID and session info
 */
export function generateOrderSeed(participantId: string, sessionInfo: {
  taskType: string;
  timestamp: number;
  congruence: string;
}): string {
  // The participant ID already supplies the random unit. Do not include the
  // calendar day: a participant who resumes after midnight must see the same
  // presentation order as before.
  return `${participantId}_${sessionInfo.taskType}_${sessionInfo.congruence}`;
}

/**
 * Email presentation order metadata for complete auditability
 */
export interface EmailPresentationOrder {
  participantId: string;
  taskType: 'email' | 'device' | 'coding';
  congruenceCondition: string;
  randomizationSeed: string;
  presentationTimestamp: number;
  emailOrder: Array<{
    emailId: string;
    presentationIndex: number; // 0-based position in shuffled array
    originalIndex: number;     // Original position in source array
    isPhishing: boolean;
    congruence: 'congruent' | 'incongruent';
    scenarioType: string;
  }>;
  orderingMetadata: {
    totalEmails: number;
    phishingCount: number;
    legitimateCount: number;
    congruentCount: number;
    incongruentCount: number;
    shuffleAlgorithm: 'fisher-yates-seeded';
    seedSource: 'participant-deterministic';
  };
}

/**
 * Create complete presentation order record
 */
export function createPresentationOrderRecord(
  participantId: string,
  taskType: 'email' | 'device' | 'coding',
  congruenceCondition: string,
  originalScenarios: any[],
  shuffledScenarios: any[],
  randomizationSeed: string
): EmailPresentationOrder {
  
  // Create mapping of original to shuffled positions
  const emailOrder = shuffledScenarios.map((scenario, shuffledIndex) => {
    const originalIndex = originalScenarios.findIndex(orig => orig.id === scenario.id);
    return {
      emailId: scenario.id,
      presentationIndex: shuffledIndex,
      originalIndex: originalIndex,
      isPhishing: scenario.isPhishing,
      congruence: scenario.congruence as 'congruent' | 'incongruent',
      scenarioType: scenario.isPhishing ? 'phishing' : 'legitimate'
    };
  });
  
  // Calculate metadata for validation
  const phishingCount = emailOrder.filter(e => e.isPhishing).length;
  const legitimateCount = emailOrder.length - phishingCount;
  const congruentCount = emailOrder.filter(e => e.congruence === 'congruent').length;
  const incongruentCount = emailOrder.filter(e => e.congruence === 'incongruent').length;
  
  return {
    participantId,
    taskType,
    congruenceCondition,
    randomizationSeed,
    presentationTimestamp: Date.now(),
    emailOrder,
    orderingMetadata: {
      totalEmails: emailOrder.length,
      phishingCount,
      legitimateCount,
      congruentCount,
      incongruentCount,
      shuffleAlgorithm: 'fisher-yates-seeded',
      seedSource: 'participant-deterministic'
    }
  };
}

/**
 * Validate email order for experimental balance
 */
export function validateEmailOrder(orderRecord: EmailPresentationOrder): {
  isValid: boolean;
  issues: string[];
  warnings: string[];
} {
  const issues: string[] = [];
  const warnings: string[] = [];
  
  const { emailOrder, orderingMetadata } = orderRecord;
  
  // The active design presents one item in each Congruence × Truth cell.
  if (orderingMetadata.totalEmails !== 4) {
    issues.push(`Expected 4 emails, got ${orderingMetadata.totalEmails}`);
  }
  
  // Check phishing/legitimate balance (2/2).
  if (orderingMetadata.phishingCount !== 2 || orderingMetadata.legitimateCount !== 2) {
    issues.push(`Expected 2 phishing + 2 legitimate, got ${orderingMetadata.phishingCount} + ${orderingMetadata.legitimateCount}`);
  }
  
  // Check congruence balance (2/2). The live condition label is
  // "balanced-2x2x2x2", while older records may use "mixed".
  if (orderingMetadata.congruentCount !== 2 || orderingMetadata.incongruentCount !== 2) {
    issues.push(`Expected 2 congruent + 2 incongruent, got ${orderingMetadata.congruentCount} + ${orderingMetadata.incongruentCount}`);
  }
  
  // Check for duplicate email IDs
  const emailIds = emailOrder.map(e => e.emailId);
  const uniqueIds = new Set(emailIds);
  if (uniqueIds.size !== emailIds.length) {
    issues.push('Duplicate email IDs found in presentation order');
  }
  
  // Check for missing presentation indices
  const presentationIndices = emailOrder.map(e => e.presentationIndex).sort();
  const expectedIndices = Array.from({length: emailOrder.length}, (_, i) => i);
  if (JSON.stringify(presentationIndices) !== JSON.stringify(expectedIndices)) {
    issues.push('Presentation indices are not sequential 0-based integers');
  }
  
  return {
    isValid: issues.length === 0,
    issues,
    warnings
  };
}
